/**
 * @file src/offline/offlineAssetIndex.js
 * @description The address book a download already wrote, read back: remote URL → logical key.
 *
 * A download stores every asset it keeps under a LOGICAL key (`offlineKeys.js`) — the texture, the
 * waveform, the cover. That is the right name for bytes that are served from a signed URL one
 * moment and a proxy the next. But nothing in the app asks for a texture by logical key: the
 * renderer starts from the remote URL the release named, and so does the waveform. With no network
 * those requests simply fail, and a fully downloaded collection draws no planet.
 *
 * Substituting a `blob:` URL at the point of resolution was tried and taken out again: the world and
 * moons managers take that value as `textureUrl` and re-resolve it against the herbarium base, so
 * the URL became `https://…herbarium…/blob:https://local…` and 403'd. **A URL here is plumbing, not
 * an address.** Whatever answers has to answer for the REAL url, which is what a service worker is
 * for — see `src/sw.js`.
 *
 * The worker needs one thing to do that: a map from the url a page will ask for to the cache entry
 * holding its bytes. Both halves are already written down. Every stored manifest records each asset
 * as `{ key, identity, url, kind }`, so this file does not invent a second record of anything — it
 * reads the manifests and turns them inside out. Nothing here opens the bucket or decides what to
 * serve; it only says which entry to look in.
 */

/**
 * The library database, named here because this module is read from the service worker, which has
 * no business importing `offlineLibraryStore.js` (owner reconciliation, release pruning, a write
 * queue — none of it belongs in a worker). That store imports these two names from here so there is
 * one declaration of where the manifests live, not two.
 */
export const OFFLINE_LIBRARY_DB_NAME = 'orbiters-offline-library';
export const OFFLINE_COLLECTIONS_STORE = 'collections';

/**
 * What a page says to the worker when it has finished changing what is downloaded.
 *
 * Declared here rather than beside either side of the conversation: the worker must not import the
 * page's registration module to learn a string, and the page must not import the worker. A listener
 * waiting on a name the sender stopped using fails silently forever, so there is one of it.
 */
export const LIBRARY_CHANGED_MESSAGE = 'offline:library-changed';
export const ASSET_BINDINGS_MESSAGE = 'offline:asset-bindings';
export const ASSET_BINDINGS_NEEDED_MESSAGE = 'offline:asset-bindings-needed';

/**
 * Kinds the worker answers for.
 *
 * Audio is deliberately absent. Playback already resolves its bytes by logical key before it
 * considers a URL (`audioAssetSource.js`), so routing the proxy would add nothing — and it would add
 * a hazard: a pinned entry is a whole 200, and answering a Range request with one is how a media
 * element breaks. The assets below are fetched whole or not at all.
 */
const SERVED_KINDS = new Set([
  'texture',
  'skybox',
  'catalogue',
  'waveform',
  'cover',
  'collection-cover',
]);

function normalize(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

/**
 * Turn stored manifests into `remote url → every cache entry that URL was stored under`.
 *
 * Takes the rows as the store holds them (a row IS a manifest, plus its record id), so this works on
 * a `getAll()` result with no reshaping.
 *
 * ## Why a LIST and not one key
 *
 * Release-aware assets can store the SAME URL under two keys — two manifests on one browser naming
 * a track cover or world texture at different releases. The worker needs the complete list to detect
 * that ambiguity and refuse a URL-only guess. A controlled page can choose one exact key from
 * authoritative release metadata.
 *
 * Owner is NOT considered, on purpose. This says where bytes are, not whose library lists them —
 * the bucket is one refcounted store shared by everyone on the machine, and the worker only ever
 * answers a request a page on this origin actually made, for a published release's public file.
 * Filtering by owner here would mean a second person's offline boot re-downloading files that are
 * already on the disk.
 *
 * @param {Array<object>} rows
 * @returns {Map<string, string[]>}
 */
export function buildPinnedAssetIndex(rows) {
  const index = new Map();
  const add = (asset) => {
    if (!asset || !SERVED_KINDS.has(asset.kind)) return;
    const url = normalize(asset.url);
    const key = normalize(asset.key);
    if (!url || !key) return;
    const keys = index.get(url);
    if (!keys) {
      index.set(url, [key]);
    } else if (!keys.includes(key)) {
      keys.push(key);
    }
  };
  for (const row of rows || []) {
    for (const asset of row?.assets || []) add(asset);
    for (const entry of row?.entries || []) {
      for (const asset of entry?.assets || []) add(asset);
    }
  }
  return index;
}

/**
 * Whether the library database exists yet.
 *
 * Asked because the read below opens WITHOUT a version — the only way to read a store at whatever
 * version the app has upgraded it to — and opening a database that does not exist CREATES it, empty,
 * at version 1. Harmless in the end (the app's own open upgrades it and creates every store), but it
 * fires an upgrade the app never asked for, and a worker that quietly creates a database the person
 * has never downloaded anything into is not the kind of surprise to leave lying around.
 *
 * `databases()` is absent in a few engines. There the honest answer is "don't know", and the caller
 * goes ahead and opens: a missing store is handled, and an empty database is recoverable.
 */
async function libraryDatabaseExists(factory) {
  if (typeof factory.databases !== 'function') return null;
  try {
    const known = await factory.databases();
    return known.some((entry) => entry?.name === OFFLINE_LIBRARY_DB_NAME);
  } catch {
    return null;
  }
}

/**
 * Read the index out of the library database.
 *
 * Read-only, version-less, and it never upgrades or repairs anything — if the store is not there,
 * there is nothing downloaded to serve and an empty map is the right answer. Never rejects: the
 * worker calls this on a fetch, and a failed read has to mean "no pinned copy", never a failed
 * request.
 *
 * @param {{ indexedDB?: IDBFactory }} [deps]
 * @returns {Promise<Map<string, string[]>>}
 */
export async function readPinnedAssetIndex({ indexedDB: factory = globalThis.indexedDB } = {}) {
  if (!factory || typeof factory.open !== 'function') return new Map();
  if ((await libraryDatabaseExists(factory)) === false) return new Map();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value, db) => {
      if (settled) return;
      settled = true;
      try {
        db?.close();
      } catch {
        // A database that will not close still gave its answer.
      }
      resolve(value);
    };

    let request;
    try {
      request = factory.open(OFFLINE_LIBRARY_DB_NAME);
    } catch {
      resolve(new Map());
      return;
    }

    request.onerror = () => finish(new Map());
    request.onblocked = () => finish(new Map());
    request.onsuccess = () => {
      const db = request.result;
      // A version-less connection blocks an upgrade from anywhere else until it closes. Reads here
      // are short, but an app tab opening at a future store version while one is mid-flight would
      // sit behind it and settle with no database at all. Let go instead: the answer this read was
      // going to give is worth less than the app's upgrade.
      db.onversionchange = () => {
        try {
          db.close();
        } catch {
          // Already closing.
        }
      };
      if (!db?.objectStoreNames?.contains(OFFLINE_COLLECTIONS_STORE)) {
        finish(new Map(), db);
        return;
      }
      try {
        const tx = db.transaction(OFFLINE_COLLECTIONS_STORE, 'readonly');
        const all = tx.objectStore(OFFLINE_COLLECTIONS_STORE).getAll();
        all.onsuccess = () => finish(buildPinnedAssetIndex(all.result || []), db);
        all.onerror = () => finish(new Map(), db);
        tx.onabort = () => finish(new Map(), db);
      } catch {
        finish(new Map(), db);
      }
    };
  });
}
